import { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { useFetchSaccoRepaymentsForLoan, useMarkSaccoInstallmentAsPaid } from "@saccoHooks";
import {SaccoLoanRepayment} from "@/@types/sacco.models.ts";

interface RepaymentScheduleProps {
  loanId: string;
  membershipId: string;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

export function RepaymentSchedule({ loanId }: RepaymentScheduleProps) {
  const { data: repayments = [], isLoading } = useFetchSaccoRepaymentsForLoan(loanId);
  const markPaid = useMarkSaccoInstallmentAsPaid();

  const [pendingInstallment, setPendingInstallment] = useState<SaccoLoanRepayment | null>(null);

  function hasUnpaidPrior(installment: SaccoLoanRepayment): boolean {
    return repayments.some(
      (r) => r.installment_number < installment.installment_number && r.is_paid === false
    );
  }

  function handleToggle(installment: SaccoLoanRepayment) {
    // Only allow marking unpaid → paid
    if (installment.is_paid) return;

    if (hasUnpaidPrior(installment)) {
      setPendingInstallment(installment);
    } else {
      confirmMarkPaid(installment);
    }
  }

  async function confirmMarkPaid(installment: SaccoLoanRepayment) {
    try {
      await markPaid.mutateAsync({
        installmentId: installment.id,
        loanId,
        paidDate: new Date().toISOString().split("T")[0],
      });
      toast.success(`Installment #${installment.installment_number} marked as paid`);
    } catch {
      toast.error("Failed to mark installment as paid");
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (repayments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        No repayment schedule found for this loan.
      </p>
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">Interest</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {repayments.map((r) => (
              <TableRow key={r.id} className={r.is_paid ? "opacity-60" : undefined}>
                <TableCell className="font-medium">{r.installment_number}</TableCell>
                <TableCell>{r.due_date}</TableCell>
                <TableCell className="text-right">{formatCurrency(Number(r.installment_amount))}</TableCell>
                <TableCell className="text-right">{formatCurrency(Number(r.principal_portion))}</TableCell>
                <TableCell className="text-right">{formatCurrency(Number(r.interest_portion))}</TableCell>
                <TableCell className="text-center">
                  {r.is_paid ? (
                    <Badge variant="secondary" className="text-green-700 bg-green-100">
                      Paid
                    </Badge>
                  ) : (
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => handleToggle(r)}
                      disabled={markPaid.isPending}
                      aria-label={`Mark installment ${r.installment_number} as paid`}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={!!pendingInstallment}
        onOpenChange={(open) => { if (!open) setPendingInstallment(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Out-of-sequence Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Installment #{pendingInstallment?.installment_number} has earlier unpaid installments.
              Marking it as paid out of order may affect your loan records. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingInstallment(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (pendingInstallment) {
                  setPendingInstallment(null);
                  await confirmMarkPaid(pendingInstallment);
                }
              }}
            >
              Mark as Paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
